/**
 * @file 表格列自定义化
 * @author fightingliu
 * @create 2022.04.18
 * @update2022.04.18
 */
import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import { Ellipsis } from '@tencent/spaui-business';
import { MathUtil, message } from '@tencent/ptp-fe-common';
import FormDialog from 'pages/manage/application/components/dialog/Form';
import NoticeDialog from 'pages/manage/application/components/dialog/Notice';
import { APPLICATION_STATUS_COLOR, AUDIT_STATUS_COLOR, Category } from 'pages/manage/application/constants';

const { thousandsSeparated } = MathUtil;

interface TableProps {
  type?: any;
  name?: any;
  value?: any | number;
  data?: any;
  options?: any;
}
// 公司规模枚举值
export const renderCompanyScale = (value: TableProps) => {
  switch (value) {
    case 1:
      return '1~10人';
    case 2:
      return '10~50人';
    case 3:
      return '50~100人';
    case 4:
      return '100~200人';
    case 5:
      return '200~500人';
    case 6:
      return '500人以上';
    default:
      return '未知';
  }
};

// 服务类型枚举值
export const renderServiceType = (value: number) => {
  switch (value) {
    case 0:
      return '投放提效工具';
    case 1:
      return '私域运营工具';
    case 2:
      return 'RTA投放工具';
    case 11:
      return '内容服务';
    case 12:
      return '数据服务';
    case 90:
      return '其他';
    case 100:
      return '未选择';
    default:
      return '未知';
  }
};

// 展示操作弹窗界面统一入口
export const showOpDialog = (
  title: string,
  submitLabel: string,
  notfiy: string,
  payload: any,
  listRef: any,
  dialogConfig: { setDialogOptions: any; hideDialog: any }
) => {
  const result = notfiy;
  const { setDialogOptions, hideDialog } = dialogConfig;
  setDialogOptions({
    config: {
      title,
      contentStyle: { width: '700px' },
      bodyStyle: { minHeight: '300px' },
      className: 'common-seat-dialog',
      submitLabel,
    },
    wrappedComponent: FormDialog,
    payload,
    closeBySuccess: false,
    onSuccess: async (ref: any) => {
      const { handleSubmit } = ref || {};
      const { loadList } = listRef.current || {};
      try {
        await handleSubmit();
        message.success(result);
        hideDialog();
        loadList?.loadList();
      } catch (error) {
        message.error(error.message);
      }
    },
  });
};

// 提示弹窗
export const showNoticeDialog = (
  payload: any,
  dialogConfig: { setDialogOptions: any; hideDialog: any },
  onSuccess: () => void,
  onCancel: () => void
) => {
  const { setDialogOptions } = dialogConfig;
  setDialogOptions({
    config: {
      className: 'common-seat-dialog',
      submitLabel: '确认',
      cancelButton: false,
      bodyStyle: { textAlign: 'center' },
      footerRightPosition: 'center',
      hideFooterDivider: true,
    },
    wrappedComponent: NoticeDialog,
    payload,
    onSuccess,
    onCancel,
  });
};
export const getCustomExtraKeyList = (category: any) => {
  if ([Category.contentService, Category.dataService, Category.daiyunying].includes(category)) {
    return [
      {
        label: '解决问题',
        name: 'solved_problem',
      },
      {
        label: '功能简述',
        name: 'features_description',
      },
      {
        label: '操作指引',
        name: 'operation_guide',
      },
      {
        label: '服务内容',
        name: 'serve_details',
      },
      {
        label: '合作流程',
        name: 'operation_flow',
      },
      {
        label: '合作案例',
        name: 'cooperation_case',
      },
      {
        label: '付费模式',
        name: 'payment_model',
      },
      {
        label: '咨询合作',
        name: 'consulting_cooperation',
      },
    ];
  }

  return [
    {
      label: '解决问题',
      name: 'solved_problem',
    },
    {
      label: '功能简述',
      name: 'features_description',
    },
    {
      label: '操作指引',
      name: 'operation_guide',
    },
    {
      label: '合作案例',
      name: 'cooperation_case',
    },
    {
      label: '付费模式',
      name: 'payment_model',
    },
    {
      label: '咨询合作',
      name: 'consulting_cooperation',
    },
  ];
};

export const renderTime = (type: TableProps, name: TableProps, value: any) => {
  return value ? moment(Date.parse(value)).format('YYYYMMDD') : '-';
};
// 整数
export const renderNormalThousandsValue = (type: TableProps, name: TableProps, value: any) => {
  if (type === 'additional' && (value === '-' || !value)) return '';
  const formatValue = value ? thousandsSeparated(value) : '';
  return <div>{formatValue || '-'}</div>;
};

export const renderThousandsValue = (type: TableProps, name: TableProps, value: any) => {
  if (type === 'additional' && (value === '-' || !value)) return '';
  const formatValue = value ? thousandsSeparated(value, 2) : '';
  return <div>{formatValue || '-'}</div>;
};

// 渲染金额
export const renderMoney = (type: TableProps, name: TableProps, value: any) => {
  if (type === 'additional' && (value === '-' || !value)) return '';
  return <span>{`${thousandsSeparated(value / 1000000, 2)}`}</span>;
};

export const renderNormalValue = (type: TableProps, name: TableProps, value: any) => {
  return <div title={value}>{value || '-'}</div>;
};

// 多出文字展示
export const renderEllipsis = (type: TableProps, name: TableProps, value: any) => {
  if (type === 'additional' && value === '-') return '';
  return (
    <Ellipsis PopOverProps={{ className: 'belong-popover' }} OverlayTriggerProps={{ placement: 'top' }} maxCount={10}>
      {value}
    </Ellipsis>
  );
};

// 状态展示并带颜色
const renderStatus = (
  type: TableProps,
  name: TableProps,
  value: TableProps,
  data: TableProps,
  options: TableProps,
  colorMap: any,
  isLabel: boolean
) => {
  const dotStyle = {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 5,
    backgroundColor: '',
  };
  const { label = '未知' } = _.find(options, [isLabel ? 'label' : 'value', value]) || {};
  const { color = '', icon = '' } = _.find(colorMap, ['label', label]) || {};
  dotStyle.backgroundColor = color;
  return (
    <div
      title={label}
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {icon ? <img src={icon} alt="" style={{ width: 16, height: 16, marginRight: 10 }} /> : <div style={dotStyle} />}
      <span style={{ color }}>{label}</span>
    </div>
  );
};

// 审核状态
export const renderAuditStatus = (
  type: TableProps,
  name: TableProps,
  value: TableProps,
  data: TableProps,
  options: TableProps,
  isLabel: boolean
) => renderStatus(type, name, value, data, options, AUDIT_STATUS_COLOR, isLabel);

// 应用状态
export const renderApplicationStatus = (
  type: TableProps,
  name: TableProps,
  value: TableProps,
  data: TableProps,
  options: TableProps,
  isLabel: boolean
) => renderStatus(type, name, value, data, options, APPLICATION_STATUS_COLOR, isLabel);

export const getLabelFromOptions = (options: any, text: any, isLabel: boolean, returnValue: boolean) => {
  const { label = '', value = '' } = _.find(options, [isLabel ? 'label' : 'value', text]) || {};
  return returnValue ? value : label;
};

export default {
  showOpDialog,
  renderCompanyScale,
  renderServiceType,
  getCustomExtraKeyList,
  getLabelFromOptions,
  renderTime,
  renderNormalThousandsValue,
  renderThousandsValue,
  renderNormalValue,
  renderMoney,
  renderAuditStatus,
  renderEllipsis,
};
